import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { isValidString } from '../../../common/CommonUtilsMethods';
import { RenameTrustVaultDocumentSubTypeAgencyUserAPICall } from '../api/TrustVaultPhiDocumentsAgencyUserAPI';
import {
    RenameTrustVaultDocumentSubTypeReqDTO,
    TrustVaultDocumentSubTypeRespDTO,
} from '../interfaces/TrustVaultPhiDocumentsAgencyUserInterfaces';

type RenameDocumentSubTypeApiCall = (
    request: RenameTrustVaultDocumentSubTypeReqDTO
) => Promise<TrustVaultDocumentSubTypeRespDTO | null>;

interface RenameTrustVaultDocumentSubTypeModalProps {
    readonly open: boolean;
    readonly fileType: TrustVaultDocumentSubTypeRespDTO | undefined;
    readonly onClose: () => void;
    readonly onSubTypeRenamed: (renamedSubType: TrustVaultDocumentSubTypeRespDTO) => void;
    readonly renameDocumentSubTypeApiCall?: RenameDocumentSubTypeApiCall;
    readonly helperText?: string;
}

const RenameTrustVaultDocumentSubTypeModal = ({
    open,
    fileType,
    onClose,
    onSubTypeRenamed,
    renameDocumentSubTypeApiCall = RenameTrustVaultDocumentSubTypeAgencyUserAPICall,
    helperText = 'The label shown when tagging worker documents',
}: RenameTrustVaultDocumentSubTypeModalProps): React.ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const [subTypeLabel, setSubTypeLabel] = useState<string>('');
    const [isRenaming, setIsRenaming] = useState<boolean>(false);

    useEffect(() => {
        if (open && fileType !== undefined) {
            setSubTypeLabel(fileType.subTypeLabel);
        }
    }, [open, fileType]);

    const handleClose = (): void => {
        if (isRenaming) {
            return;
        }
        setSubTypeLabel('');
        onClose();
    };

    const handleRenameClick = async (): Promise<void> => {
        if (fileType === undefined) {
            return;
        }

        const trimmedLabel: string = subTypeLabel.trim();
        if (!isValidString(trimmedLabel)) {
            enqueueSnackbar('File type label is required', { variant: 'error' });
            return;
        }

        setIsRenaming(true);
        try {
            const renamedSubType = await renameDocumentSubTypeApiCall({
                subTypeUniqueIdentifier: fileType.subTypeUniqueIdentifier,
                subTypeLabel: trimmedLabel,
            });
            if (renamedSubType === null) {
                enqueueSnackbar('Unable to rename file type', { variant: 'error' });
                return;
            }
            enqueueSnackbar('File type renamed', { variant: 'success' });
            setSubTypeLabel('');
            onSubTypeRenamed(renamedSubType);
            onClose();
        } catch (error) {
            console.error('Failed to rename TrustVault document sub-type:', error);
            enqueueSnackbar('Unable to rename file type', { variant: 'error' });
        } finally {
            setIsRenaming(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Rename file type</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="File type label"
                    size="small"
                    fullWidth
                    value={subTypeLabel}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setSubTypeLabel(event.target.value);
                    }}
                    disabled={isRenaming}
                    helperText={helperText}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isRenaming}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={(): void => {
                        void handleRenameClick();
                    }}
                    disabled={isRenaming || !isValidString(subTypeLabel.trim())}
                >
                    {isRenaming ? <CircularProgress size={18} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { RenameTrustVaultDocumentSubTypeModal };
